/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React from 'react';
import { Scale, Landmark, Globe, BookOpen, AlertCircle, Sparkles, Send, CheckCircle, FileText, Briefcase, Users, PieChart, Map, HeartPulse, Vote, Ship, Building2, MapPin, Compass, Calendar, Building, FileBarChart, Lock } from 'lucide-react';
import { LegalDocument, LegalOrigin, LegalDomain, SuggestedDocument } from '../../types';
import { StatusBadge } from '../ui/StatusBadge';
import { formatFrenchDate, formatDomain } from '../../utils/legalUtils';
import { documentService, searchService } from '../../Services';
import heroBg from '../ui/bg.jpg';
import bg2 from '../ui/bg2.jpg';
import { SearchAutocomplete } from '../ui/SearchAutocomplete';
import { useAppDispatch, useAppSelector } from '../../ReduxToolkit/Hooks';
import { useNavigate } from 'react-router-dom';
import { fetchCategoriesDropdown } from '../../ReduxToolkit/Category/CategorySlice';
import { fetchAreaOfLawDropdown } from '../../ReduxToolkit/AreaOfLaw/AreaOfLawSlice';
import { useAuth } from '../../Hooks/useAuth';
import { useAuthModal } from '../../contexts/AuthModalContext';
import { usePermissions } from '../../Hooks/usePermissions';
import { AiModeToggle, LegalQuestionInput, AiAccessDeniedModal } from '../ai';

interface HomeViewProps {
  documents: LegalDocument[];
  setView: (view: string) => void;
  setActiveDocumentId: (id: number | null) => void;
  setSearchQueryState: (q: string) => void;
  setSearchFiltersState: (filters: any) => void;
  addSuggestion: (suggestion: Omit<SuggestedDocument, 'id' | 'status' | 'submittedAt'> & { contactEmail?: string }) => Promise<any> | any;
}

export const HomeView: React.FC<HomeViewProps> = ({
  documents,
  setView,
  setActiveDocumentId,
  setSearchQueryState,
  setSearchFiltersState,
  addSuggestion
}) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { isAuthenticated } = useAuth();
  const { openAuthModal } = useAuthModal();
  const { dropdownItems: categoryItems, loading: categoryLoading } = useAppSelector((state) => state.category);
  const { dropdownItems: areaOfLawItems, loading: areaOfLawLoading } = useAppSelector((state) => state.areaOfLaw);

  React.useEffect(() => {
    dispatch(fetchCategoriesDropdown());
    dispatch(fetchAreaOfLawDropdown());
  }, [dispatch]);

  const { hasPermission } = usePermissions();
  const currentUser = useAppSelector((state) => state.auth.currentUser);
  const isAdmin = currentUser?.role === 'ADMIN';
  const hasAiPrivilege = hasPermission('use_ai_assistant') || isAdmin;

  // Mode Assistant Juridique IA
  const [isAiMode, setIsAiMode] = React.useState(false);
  const [accessModalOpen, setAccessModalOpen] = React.useState(false);
  const [accessModalMode, setAccessModalMode] = React.useState<'IN_DEVELOPMENT' | 'UNAUTHENTICATED' | 'NO_PRIVILEGE'>('IN_DEVELOPMENT');

  const handleToggleAiMode = () => {
    setAccessModalMode('IN_DEVELOPMENT');
    setAccessModalOpen(true);
  };

  const handleAiQuestionSubmit = (questionText: string) => {
    navigate('/search', { state: { aiQuestion: questionText, aiMode: true } });
  };

  const [searchInput, setSearchInput] = React.useState('');
  
  // Advanced search states
  const [showAdvanced, setShowAdvanced] = React.useState(false);
  const [filterNature, setFilterNature] = React.useState('');
  const [filterDomain, setFilterDomain] = React.useState('');
  const [filterRef, setFilterRef] = React.useState('');
  const [filterYear, setFilterYear] = React.useState('');
  const [availableYears, setAvailableYears] = React.useState<number[]>([]);

  React.useEffect(() => {
    searchService.getDocumentYears().then(setAvailableYears).catch(console.error);
  }, []);

  // dynamic naturesList from redux state
  const naturesList = categoryItems || [];

  // dynamic domainsList from redux state
  const domainsList = areaOfLawItems || [];

  // Liste unifiée des natures d'actes issues de la base de données
  const availableNatures = React.useMemo(() => {
    if (naturesList && naturesList.length > 0) {
      return naturesList.map((n: any) => {
        const val = (n.value || n.label || n.name || '').trim().toUpperCase();
        const lbl = (n.label || n.name || n.value || '').trim();
        return { value: val, label: lbl };
      }).filter(n => n.value);
    }
    return [
      { value: 'ARRETE', label: 'ARRÊTÉ' },
      { value: 'LOI', label: 'LOI' },
      { value: 'DECRET', label: 'DÉCRET' },
      { value: 'ORDONNANCE', label: 'ORDONNANCE' },
      { value: 'CIRCULAIRE', label: 'CIRCULAIRE' },
      { value: 'DECISION', label: 'DÉCISION' },
      { value: 'CONSTITUTION', label: 'CONSTITUTION' },
      { value: 'TRAITE', label: 'TRAITÉ / CONVENTION' },
      { value: 'JURISPRUDENCE', label: 'JURISPRUDENCE' },
      { value: 'AUTRE', label: 'AUTRE ACTE' }
    ];
  }, [naturesList]);

  // Suggestion box state
  const [suggType, setSuggType] = React.useState<'TEXTE_MANQUANT' | 'ERREUR_TEXTE' | 'AUTRE'>('TEXTE_MANQUANT');
  const [suggNature, setSuggNature] = React.useState('ARRETE');

  // Synchroniser avec la première nature disponible dès le chargement de la BD
  React.useEffect(() => {
    if (availableNatures.length > 0 && (!suggNature || !availableNatures.some(n => n.value === suggNature))) {
      setSuggNature(availableNatures[0].value);
    }
  }, [availableNatures, suggNature]);
  const [suggRef, setSuggRef] = React.useState('');
  const [suggTitle, setSuggTitle] = React.useState('');
  const [suggDesc, setSuggDesc] = React.useState('');
  const [suggEmail, setSuggEmail] = React.useState('');
  const [suggSubmitted, setSuggSubmitted] = React.useState(false);
  const [lastTicketId, setLastTicketId] = React.useState<string>('');

  // Submit missing document recommendation
  const handleSuggestSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!suggRef.trim() || !suggTitle.trim()) {
      alert("Veuillez renseigner au moins la référence légale et le titre de l'acte.");
      return;
    }
    
    const extraDesc = suggEmail.trim()
      ? `${suggDesc.trim() ? suggDesc.trim() + " — " : ""}Contact déclarant : ${suggEmail.trim()}`
      : suggDesc.trim();

    const created = addSuggestion({
      type: suggType,
      nature: suggNature || 'ARRETE',
      reference: suggRef.trim(),
      title: suggTitle.trim(),
      description: extraDesc
    });

    const ticketId = created?.id || `DOC-2026-${Math.floor(1000 + Math.random() * 9000)}`;
    setLastTicketId(ticketId);
    setSuggSubmitted(true);
    setSuggRef('');
    setSuggTitle('');
    setSuggDesc('');
    setSuggEmail('');
  };

  // Quick action to search
  const handleHeroSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchInput.trim() || filterNature || filterDomain || filterRef || filterYear) {
      const queryText = [searchInput, filterRef].filter(Boolean).join(' ');
      setSearchQueryState(queryText);
      
      const newFilters: any = {};
      if (filterNature) newFilters.nature = filterNature;
      if (filterDomain) newFilters.areaOfLaw = filterDomain;
      if (filterYear) newFilters.effectiveYear = Number(filterYear);
      
      setSearchFiltersState(newFilters);
      setView('search');
    }
  };

  const triggerQuickSearch = (term: string) => {
    setSearchQueryState(term);
    setSearchFiltersState({});
    setView('search');
  };

  const triggerOriginSearch = (origin: LegalOrigin, _label: string) => {
    setSearchQueryState('');
    setSearchFiltersState({ legalOrigin: origin });
    setView('search');
  };

  const triggerDomainSearch = (domain: LegalDomain, _label: string) => {
    setSearchQueryState('');
    setSearchFiltersState({ areaOfLaw: domain });
    setView('search');
  };

  // Get the 5 most recently registered documents from API
  const [latestUpdates, setLatestUpdates] = React.useState<LegalDocument[]>([]);

  React.useEffect(() => {
    documentService.getDocuments(5).then(setLatestUpdates);
  }, []);

  const stats = [
    { value: "25 000+", label: "Actes juridiques indexés", desc: "Depuis l'indépendance de 1960" },
    { value: "1 960+", label: "Journaux Officiels numérisés", desc: "Historique intégral d'archives" },
    { value: "630+", label: "Textes & Décisions OHADA", desc: "Jurisprudence & Actes Uniformes" },
    { value: "224+", label: "RÈGLEMENTS CEMAC", desc: "Règles bancaires de la sous-région" },
    { value: "300+", label: "Traités Internationaux", desc: "Conventions & accords ratifiés" }
  ];

  const domains: { name: string; key: LegalDomain; icon: React.ReactNode }[] = [
    { name: "Droit Pénal", key: "DROIT_PENAL", icon: <Scale className="w-8 h-8 text-indigo-500" /> },
    { name: "Droit Civil", key: "DROIT_CIVIL", icon: <FileText className="w-8 h-8 text-emerald-500" /> },
    { name: "Droit Commercial", key: "DROIT_COMMERCIAL", icon: <Briefcase className="w-8 h-8 text-amber-500" /> },
    { name: "Droit du Travail", key: "DROIT_TRAVAIL", icon: <Users className="w-8 h-8 text-rose-500" /> },
    { name: "Droit Fiscal", key: "DROIT_FISCAL", icon: <PieChart className="w-8 h-8 text-sky-500" /> },
    { name: "Droit Foncier", key: "DROIT_FONCIER", icon: <Map className="w-8 h-8 text-lime-500" /> },
    { name: "Droit Social", key: "DROIT_SOCIAL", icon: <HeartPulse className="w-8 h-8 text-red-400" /> },
    { name: "Droit Administratif", key: "DROIT_ADMINISTRATIF", icon: <Landmark className="w-8 h-8 text-zinc-500" /> },
    { name: "Droit constitutionnel", key: "DROIT_CONSTITUTIONNEL", icon: <Vote className="w-8 h-8 text-purple-500" /> },
    { name: "Droit Douanier", key: "DROIT_DOUANIER", icon: <Ship className="w-8 h-8 text-blue-500" /> },
    { name: "Droit Bancaire", key: "DROIT_BANQUE", icon: <Building2 className="w-8 h-8 text-green-600" /> },
    { name: "Droit International", key: "DROIT_INTERNATIONAL", icon: <Globe className="w-8 h-8 text-blue-400" /> }
  ];

  return (
    <div className="flex flex-col min-h-screen bg-zinc-50/40">
      
      {/* SECTION 1 — HERO SECTION DECORATED WITH DOCARN BRAND IDENTITY */}
      <section
        className="relative py-20 px-4 border-b border-primary-750/10"
        style={{
          backgroundImage: `url(${heroBg})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      >
        {/* Dark violet overlay for text readability */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#1C0132]/80 via-[#3D0F73]/70 to-[#1C0132]/85"></div>
        {/* Subtle geometric grid line overlay */}
        {/* Subtle dot pattern overlay */}
        <div className="absolute inset-0 opacity-10 bg-[radial-gradient(rgba(255,255,255,0.4)_1px,transparent_1px)] [background-size:32px_32px]"></div>
        
        <div className="max-w-4xl mx-auto text-center relative z-10 space-y-6">
          <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 backdrop-blur-sm px-3.5 py-1 rounded-full text-orange-light text-[10px] uppercase tracking-wider font-semibold">
            <Sparkles className="w-3.5 h-3.5" /> Portail Juridique Officiel Camerounais
          </div>
          
          <h1 className="font-display text-4xl sm:text-5xl lg:text-5xl font-extrabold tracking-tight text-white leading-tight drop-shadow-lg">
            La Référence du <span className="text-orange-light italic font-serif">Droit Camerounais</span>
          </h1>
          
          <p className="text-white/75 text-xs sm:text-sm max-w-2xl mx-auto font-normal leading-relaxed">
            Accédez instantanément à plus de <strong className="text-white font-semibold">25.000 textes et actes juridiques en vigueur au Cameroun</strong> : Législation nationale, Droit communautaire et Traités internationaux depuis 1960.
          </p>

          {/* Hero search — Google-style autocomplete ou Assistant IA */}
          <div className="max-w-2xl mx-auto space-y-3 mt-7">
            {isAiMode ? (
              <div className="relative z-30 space-y-3">
                <LegalQuestionInput
                  value={searchInput}
                  onChange={setSearchInput}
                  onSubmit={handleAiQuestionSubmit}
                  variant="hero"
                />
                <div className="flex items-center justify-start px-1">
                  <AiModeToggle
                    isAiMode={isAiMode}
                    onToggle={handleToggleAiMode}
                    variant="hero"
                  />
                </div>
              </div>
            ) : (
              /* Autocomplete wraps both the input and the dropdown */
              <div className="relative z-30">
                <SearchAutocomplete
                  id="hero-search-bar"
                  value={searchInput}
                  onChange={setSearchInput}
                  onSearch={(term) => {
                    setSearchQueryState(term);
                    setSearchFiltersState({});
                    setView('search');
                  }}
                  documents={documents}
                  placeholder="Rechercher par expression libre (ex: 'bail commercial', 'huissier')..."
                  variant="hero"
                />

                {/* Advanced-filters toggle + Toggle IA sur la MÊME LIGNE */}
                <div className="flex flex-wrap items-center justify-between gap-2 mt-3 px-1">
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setShowAdvanced(!showAdvanced)}
                      className={`flex items-center gap-1.5 px-4 py-2 border rounded-full text-[10px] font-bold uppercase tracking-wider transition-all cursor-pointer ${
                        showAdvanced
                          ? 'bg-orange-light/10 border-orange-light text-orange-light'
                          : 'bg-white/80 border-white/40 text-black hover:bg-white hover:text-zinc-700'
                      }`}
                    >
                      Options {showAdvanced ? '▲' : '▼'}
                    </button>

                    <AiModeToggle
                      isAiMode={isAiMode}
                      onToggle={handleToggleAiMode}
                      variant="hero"
                    />
                  </div>

                  <button
                    type="button"
                    onClick={() => {
                      if (searchInput.trim() || filterNature || filterDomain || filterRef || filterYear) {
                        if (!isAuthenticated && (filterNature || filterDomain || filterYear)) {
                          openAuthModal();
                          return;
                        }
                        const queryText = [searchInput, filterRef].filter(Boolean).join(' ');
                        setSearchQueryState(queryText);
                        const newFilters: any = {};
                        if (isAuthenticated) {
                          if (filterNature) newFilters.nature = filterNature;
                          if (filterDomain) newFilters.areaOfLaw = filterDomain;
                          if (filterYear) newFilters.effectiveYear = Number(filterYear);
                        }
                        setSearchFiltersState(newFilters);
                        setView('search');
                      }
                    }}
                    className="bg-violet-medium hover:bg-violet-dark text-white font-semibold px-6 py-2.5 rounded-full text-xs tracking-wider uppercase transition-colors cursor-pointer border-none shadow-lg"
                  >
                    Rechercher
                  </button>
                </div>
              </div>
            )}

            {/* EXPANDABLE MULTI-CRITERIA SEARCH GRID */}
            {showAdvanced && !isAiMode && (
              <div className="p-6 bg-white border border-zinc-200 rounded-3xl shadow-lg space-y-4 animate-fade-in text-left">
                <div className="flex items-center justify-between border-b border-zinc-100 pb-2">
                  <h4 className="text-xs uppercase font-bold text-slate-800 tracking-wider flex items-center gap-1.5">
                    Critères de recherche approfondis
                  </h4>
                  {!isAuthenticated && (
                    <span className="text-[10px] text-slate-700 bg-slate-100 border border-slate-200 px-2.5 py-0.5 rounded-md font-semibold flex items-center gap-1">
                      <Lock className="w-3 h-3 text-slate-500" /> Abonnés
                    </span>
                  )}
                </div>

                {!isAuthenticated && (
                  <div className="p-3.5 bg-slate-50 border border-slate-200 rounded-xl flex items-center justify-between text-xs text-slate-700">
                    <span className="text-[11px] font-medium flex items-center gap-1.5">
                      <Lock className="w-3.5 h-3.5 text-slate-500 shrink-0" />
                      <span>Le filtrage combiné par nature et domaine nécessite un compte abonné.</span>
                    </span>
                    <button
                      type="button"
                      onClick={() => openAuthModal()}
                      className="ml-3 shrink-0 px-3 py-1.5 bg-slate-900 text-white rounded-lg text-[10px] font-bold uppercase tracking-wider hover:bg-slate-800 transition-colors"
                    >
                      S'abonner
                    </button>
                  </div>
                )}
                
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 text-xs">
                  {/* Nature Selection */}
                  <div className="space-y-1">
                    <label className="block text-[10px] font-bold uppercase text-zinc-500 tracking-wider">Nature de l'acte</label>
                    <select
                      disabled={!isAuthenticated}
                      value={filterNature}
                      onChange={(e) => setFilterNature(e.target.value)}
                      className={`w-full p-2.5 border rounded-xl focus:outline-none text-zinc-700 font-semibold ${
                        !isAuthenticated 
                          ? 'bg-zinc-100 border-zinc-200 text-zinc-400 cursor-not-allowed opacity-60' 
                          : 'bg-zinc-50 border-zinc-200 focus:border-slate-400'
                      }`}
                    >
                      <option value="">-- Toutes --</option>
                      {categoryLoading && <option value="" disabled>Chargement...</option>}
                      {availableNatures.map((n) => (
                        <option 
                          key={n.value} 
                          value={n.value}
                        >
                          {n.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Domain Selection */}
                  <div className="space-y-1">
                    <label className="block text-[10px] font-bold uppercase text-zinc-500 tracking-wider">Domaine Juridique</label>
                    <select
                      disabled={!isAuthenticated}
                      value={filterDomain}
                      onChange={(e) => setFilterDomain(e.target.value)}
                      className={`w-full p-2.5 border rounded-xl focus:outline-none text-zinc-700 font-semibold ${
                        !isAuthenticated 
                          ? 'bg-zinc-100 border-zinc-200 text-zinc-400 cursor-not-allowed opacity-60' 
                          : 'bg-zinc-50 border-zinc-200 focus:border-slate-400'
                      }`}
                    >
                      <option value="">-- Tous --</option>
                      {areaOfLawLoading && <option value="" disabled>Chargement...</option>}
                      {domainsList.map((d: any) => (
                        <option 
                          key={d.value || d.id} 
                          value={d.value || d.id}
                        >
                          {d.label || d.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Exact Reference */}
                  <div className="space-y-1">
                    <label className="block text-[10px] font-bold uppercase text-zinc-500 tracking-wider">Référence / Numéro</label>
                    <input
                      type="text"
                      placeholder="Ex: N°2023/042"
                      value={filterRef}
                      onChange={(e) => setFilterRef(e.target.value)}
                      className="w-full p-2.5 bg-zinc-50 border border-zinc-200 rounded-xl focus:outline-none focus:border-violet-medium text-zinc-700"
                    />
                  </div>

                  {/* Specific Year */}
                  <div className="space-y-1">
                    <label className="block text-[10px] font-bold uppercase text-zinc-500 tracking-wider">Année d'adoption</label>
                    <select
                      disabled={!isAuthenticated}
                      value={filterYear}
                      onChange={(e) => setFilterYear(e.target.value)}
                      className={`w-full p-2.5 border rounded-xl focus:outline-none text-zinc-700 font-semibold ${
                        !isAuthenticated 
                          ? 'bg-zinc-100 border-zinc-200 text-zinc-400 cursor-not-allowed opacity-60' 
                          : 'bg-zinc-50 border-zinc-200 focus:border-violet-medium'
                      }`}
                    >
                      <option value="">-- Toutes les années --</option>
                      {availableYears.map(y => (
                        <option key={y} value={y}>{y}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="flex justify-end gap-2 pt-2 border-t border-zinc-100">
                  <button
                    type="button"
                    onClick={() => {
                      setFilterNature('');
                      setFilterDomain('');
                      setFilterRef('');
                      setFilterYear('');
                    }}
                    className="px-4 py-2 border border-zinc-250 hover:bg-zinc-50 rounded-xl text-[10px] uppercase font-bold tracking-wider text-zinc-650 cursor-pointer transition-colors"
                  >
                    Effacer
                  </button>
                  <button
                    type="button"
                    onClick={handleHeroSearch}
                    className="px-5 py-2 bg-violet-medium hover:bg-violet-dark text-white rounded-xl text-[10px] uppercase font-bold tracking-wider cursor-pointer transition-colors border-none"
                  >
                    Rechercher
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Quick searches shortcuts */}
          <div className="flex flex-wrap items-center justify-center gap-1.5 pt-2 text-[11px]">
            <span className="font-medium text-white/50">Recherches rapides :</span>
            {[
              "Code Pénal 2016",
              "Droit Foncier",
              "Acte Uniforme OHADA",
              "Code du Travail",
              "Décret Huissier"
            ].map((term) => (
              <button
                key={term}
                onClick={() => triggerQuickSearch(term)}
                className="bg-white/10 hover:bg-orange-light/20 border border-white/20 hover:border-orange-light/50 px-2.5 py-0.5 rounded-full transition-colors text-[10px] text-white/80 hover:text-orange-light cursor-pointer backdrop-blur-sm"
              >
                {term}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 2 — STATISTICS COUNTERS — VIOLET BG / WHITE TEXT */}
      <section className="py-12 px-4 border-b border-violet-dark/30" style={{ background: 'linear-gradient(135deg, #3D0F73 0%, #8320df 50%, #3D0F73 100%)' }}>
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6 text-center">
            {stats.map((st, idx) => (
              <div key={idx} className="p-4 rounded-2xl hover:bg-white/10 transition-colors border border-transparent hover:border-white/20">
                <span className="block font-display text-3xl sm:text-4xl font-extrabold mb-1" style={{ color: '#ffa15e' }}>
                  {st.value}
                </span>
                <span className="block font-semibold text-[11px] sm:text-xs text-white uppercase tracking-wider">
                  {st.label}
                </span>
                <span className="block text-[10px] text-white/60 mt-0.5 font-light">
                  {st.desc}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 3 — THE 3 PORTALS MAPPED UP TO THE THEORETICAL FOUNDATION / HIÉRARCHIE DE KELSEN */}
      <section className="py-16 px-4 max-w-7xl mx-auto">
        <div className="text-center space-y-2 mb-12">
          <h2 className="font-display text-3xl font-bold tracking-tight text-primary-950">
            Explorer par Origine Législative
          </h2>
          <p className="text-stone-500 text-xs sm:text-sm max-w-lg mx-auto">
            La clé d'accès structurée pour naviguer instantanément suivant la pyramide de Kelsen.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Card 1: National Portal */}
          <div className="bg-white border border-zinc-200/85 rounded-2xl flex flex-col justify-between group transition-all hover:border-primary-750/30 hover:shadow-lg/5 duration-300">
            <div className="p-6 space-y-4">
              <div className="bg-zinc-50 text-primary-750 rounded-full p-3 w-12 h-12 flex items-center justify-center group-hover:bg-primary-50 transition-colors duration-300">
                <Landmark className="w-5 h-5" />
              </div>
              <div className="space-y-1">
                <h3 className="font-display text-lg font-bold text-zinc-950 flex items-center gap-2">
                  Textes Nationaux
                </h3>
                <span className="text-[10px] text-zinc-500 tracking-wider font-semibold uppercase">Souveraineté Nationale</span>
              </div>
              <p className="text-xs text-zinc-600 leading-relaxed font-normal">
                Règles de droit internes créées par l'Etat du Cameroun : Constitution, Lois, Ordonnances, Décrets, Arrêtés, Circulaires...
              </p>
              <div className="border-t border-zinc-100 pt-3 flex flex-wrap gap-1.5">
                {["Constitution", "Lois", "Ordonnances", "Décrets", "Arrêtés", "Circulaires"].map((tag) => (
                  <button 
                    key={tag} 
                    type="button"
                    onClick={() => triggerQuickSearch(tag)}
                    className="text-[9px] font-mono tracking-wide bg-zinc-50 hover:bg-primary-50 hover:text-primary-750 border border-zinc-100 text-zinc-650 px-2 py-0.5 rounded-full cursor-pointer transition-colors"
                  >
                    {tag}
                  </button>
                ))}
              </div>
            </div>
            <div className="p-6 pt-0 flex items-center justify-between">
              <button
                onClick={() => triggerOriginSearch('NATIONAL', 'Textes Nationaux')}
                className="text-primary-750 text-[11px] font-bold uppercase tracking-wider hover:text-primary-900 flex items-center gap-1 cursor-pointer transition-colors"
              >
                Explorer la Législation →
              </button>
            </div>
          </div>

          {/* Card 2: Community Portal */}
          <div className="bg-white border border-zinc-200/85 rounded-2xl flex flex-col justify-between group transition-all hover:border-primary-750/30 hover:shadow-lg/5 duration-300">
            <div className="p-6 space-y-4">
              <div className="bg-zinc-50 text-primary-750 rounded-full p-3 w-12 h-12 flex items-center justify-center group-hover:bg-primary-50 transition-colors duration-300">
                <BookOpen className="w-5 h-5" />
              </div>
              <div className="space-y-1">
                <h3 className="font-display text-lg font-bold text-zinc-950 flex items-center gap-2">
                  Textes Communautaires
                </h3>
                <span className="text-[10px] text-zinc-500 tracking-wider font-semibold uppercase">Harmonisation Continentale</span>
              </div>
              <p className="text-xs text-zinc-600 leading-relaxed font-normal">
                Textes adoptés à l'échelle africaine et applicables au Cameroun : Règlements CEMAC, Actes Uniformes OHADA, Accord de Bangui, Charte Africaine des Droits de l'Homme et des Peuples.
              </p>
              <div className="border-t border-zinc-100 pt-3 flex flex-wrap gap-1.5">
                {["Règlements CEMAC", "OHADA", "Accord Bangui", "Charte Africaine"].map((tag) => (
                  <button 
                    key={tag} 
                    type="button"
                    onClick={() => triggerQuickSearch(tag)}
                    className="text-[9px] font-mono tracking-wide bg-zinc-50 hover:bg-primary-50 hover:text-primary-750 border border-zinc-100 text-zinc-650 px-2 py-0.5 rounded-full cursor-pointer transition-colors"
                  >
                    {tag}
                  </button>
                ))}
              </div>
            </div>
            <div className="p-6 pt-0 flex items-center justify-between">
              <button
                onClick={() => triggerOriginSearch('COMMUNITY', 'Textes Régionaux OHADA CEMAC')}
                className="text-primary-750 text-[11px] font-bold uppercase tracking-wider hover:text-primary-900 flex items-center gap-1 cursor-pointer transition-colors"
              >
                Explorer l'espace Régional →
              </button>
            </div>
          </div>

          {/* Card 3: International Portal */}
          <div className="bg-white border border-zinc-200/85 rounded-2xl flex flex-col justify-between group transition-all hover:border-primary-750/30 hover:shadow-lg/5 duration-300">
            <div className="p-6 space-y-4">
              <div className="bg-zinc-50 text-primary-750 rounded-full p-3 w-12 h-12 flex items-center justify-center group-hover:bg-primary-50 transition-colors duration-300">
                <Globe className="w-5 h-5" />
              </div>
              <div className="space-y-1">
                <h3 className="font-display text-lg font-bold text-zinc-950 flex items-center gap-2">
                  Textes Internationaux
                </h3>
                <span className="text-[10px] text-zinc-500 tracking-wider font-semibold uppercase">Traités Internationaux</span>
              </div>
              <p className="text-xs text-zinc-600 leading-relaxed font-normal">
                Accords bilatéraux et multilatéraux signés ou ratifiés par l'Etat du Cameroun : Convention des Nations Unies contre la corruption, conventions de l'OIT, Convention de Montréal de 1999.
              </p>
              <div className="border-t border-zinc-100 pt-3 flex flex-wrap gap-1.5">
                {["Conventions", "Traités OIT", "Nations Unies"].map((tag) => (
                  <button 
                    key={tag} 
                    type="button"
                    onClick={() => triggerQuickSearch(tag)}
                    className="text-[9px] font-mono tracking-wide bg-zinc-50 hover:bg-primary-50 hover:text-primary-750 border border-zinc-100 text-zinc-650 px-2 py-0.5 rounded-full cursor-pointer transition-colors"
                  >
                    {tag}
                  </button>
                ))}
              </div>
            </div>
            <div className="p-6 pt-0 flex items-center justify-between">
              <button
                onClick={() => triggerOriginSearch('INTERNATIONAL', 'Traités Internationaux')}
                className="text-primary-750 text-[11px] font-bold uppercase tracking-wider hover:text-primary-900 flex items-center gap-1 cursor-pointer transition-colors"
              >
                Explorer les Traités →
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 4 — LATEST UPDATES TIMELINE MAPPED IN ACCORDANCE WITH SECTION 8.4 */}
      <section 
        className="relative py-20 px-4 border-t border-b border-primary-750/10 overflow-hidden bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${bg2})` }}
      >
        {/* Semi-transparent overlay to ensure text readability */}
        <div className="absolute inset-0 bg-black/60 backdrop-blur-[2px] pointer-events-none"></div>

        <div className="max-w-5xl mx-auto space-y-10 relative z-10">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
            <div className="space-y-2">
              <h2 className="font-display text-3xl font-extrabold text-white tracking-tight">
                Derniers Textes & <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-400 to-primary-200">Mises-à-Jour</span>
              </h2>
              <p className="text-zinc-300 text-sm font-medium">Les modifications législatives récentes appliquées par notre équipe de documentalistes.</p>
            </div>
            <button
              onClick={() => {
                setSearchQueryState('');
                setSearchFiltersState({});
                setView('search');
              }}
              className="px-5 py-2.5 bg-black/30 backdrop-blur-sm border border-white/20 text-white hover:bg-white/20 shadow-sm hover:shadow-md text-xs font-bold uppercase tracking-wider rounded-full transition-all cursor-pointer focus:outline-none"
            >
              Toutes les archives →
            </button>
          </div>

          <div className="space-y-4">
            {latestUpdates.map((doc) => (
              <div
                key={doc.id}
                onClick={() => {
                  if (isAuthenticated) {
                    navigate(`/document/${doc.id}`, { state: { document: doc } });
                  } else {
                    setSearchQueryState(doc.officialTitle);
                    setSearchFiltersState({});
                    setView('search');
                  }
                }}
                className="group bg-black/40 backdrop-blur-md p-6 rounded-2xl border border-white/10 shadow-sm hover:shadow-xl hover:shadow-black/20 transition-all duration-300 flex flex-col sm:flex-row sm:items-center justify-between gap-6 hover:-translate-y-0.5 cursor-pointer"
              >
                <div className="space-y-3 flex-grow">
                  <div className="flex flex-wrap items-center gap-2">
                    {doc.nature && (
                      <span className="text-[10px] font-mono font-semibold bg-slate-800 text-white px-2.5 py-0.5 rounded-md shadow-sm">
                        {doc.nature}
                      </span>
                    )}
                    <span className="text-[10px] font-mono font-medium bg-black/30 text-zinc-300 border border-white/10 px-2 py-0.5 rounded-md flex items-center gap-1">
                      {doc.referenceNumber ? `Réf: ${doc.referenceNumber}` : 'Réf. consultable abonnés'}
                    </span>
                    <StatusBadge status={doc.legalStatus} showIcon={true} size="sm" />
                    {!isAuthenticated && (
                      <span className="text-[10px] font-medium bg-white/10 text-zinc-300 border border-white/10 px-2 py-0.5 rounded-md flex items-center gap-1">
                        <Lock className="w-2.5 h-2.5 text-zinc-400" /> Indexé
                      </span>
                    )}
                  </div>
                  <h3 className="font-display text-lg font-bold text-white line-clamp-2 leading-snug group-hover:text-primary-200 transition-colors">
                    {doc.officialTitle}
                  </h3>
                  <div className="flex flex-wrap items-center gap-3 text-xs text-zinc-400 font-medium">
                    <span className="flex items-center gap-1.5 bg-black/30 px-2 py-1 rounded-md border border-white/10">
                      <Calendar className="w-3.5 h-3.5 text-zinc-400" /> Adopté le : <strong className="text-zinc-200">{formatFrenchDate(doc.dateOfAdoption) || (doc.isLocked ? "Consultable abonnés" : "N/A")}</strong>
                    </span>
                    <span className="flex items-center gap-1.5 bg-black/30 px-2 py-1 rounded-md border border-white/10">
                      <Building className="w-3.5 h-3.5 text-zinc-400" /> Émetteur: <strong className="text-zinc-200">{doc.issuingAuthority || (doc.isLocked ? "Consultable abonnés" : "N/A")}</strong>
                    </span>
                    {doc.totalPages ? (
                      <span className="flex items-center gap-1.5 bg-black/30 px-2 py-1 rounded-md border border-white/10">
                        <FileBarChart className="w-3.5 h-3.5 text-zinc-400" /> Pages: <strong className="text-zinc-200">{doc.totalPages}</strong>
                      </span>
                    ) : null}
                  </div>
                </div>

                <div className="shrink-0 flex sm:flex-col items-end gap-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (isAuthenticated) {
                        navigate(`/document/${doc.id}`, { state: { document: doc } });
                      } else {
                        setSearchQueryState(doc.officialTitle);
                        setSearchFiltersState({});
                        setView('search');
                      }
                    }}
                    className="w-full sm:w-auto text-center px-6 py-2.5 bg-primary-600 text-white group-hover:bg-primary-500 font-bold rounded-xl text-xs transition-all duration-300 cursor-pointer shadow-md"
                  >
                    {isAuthenticated ? "Consulter" : "Rechercher l'acte →"}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 5 — LEGAL DOMAINS THEMATIC MATRIX GRID AS DESCRIBED IN SECTION 8.5 */}
      <section className="py-20 px-4 max-w-7xl mx-auto">
        <div className="text-center space-y-2 mb-12">
          <h2 className="font-display text-2xl font-bold tracking-tight text-zinc-950">
            Explorer par Domaines Thématiques
          </h2>
          <p className="text-zinc-500 text-xs sm:text-sm max-w-lg mx-auto">
            Accédez directement aux textes sectoriels en un seul clic à travers nos 12 divisions doctrinales.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {domains.map((dom) => (
            <button
              key={dom.key}
              onClick={() => triggerDomainSearch(dom.key, dom.name)}
              className="flex flex-col items-center justify-center p-6 bg-white border border-zinc-200/80 rounded-2xl hover:border-primary-750 hover:shadow-lg/5 transition-all text-center group cursor-pointer"
              id={`domain-search-${dom.key}`}
            >
              <span className="text-3xl mb-3 group-hover:scale-105 transition-transform">
                {dom.icon}
              </span>
              <span className="font-semibold text-xs text-zinc-750 block line-clamp-2">
                {dom.name}
              </span>
            </button>
          ))}
        </div>
      </section>

      {/* SECTION 6 — USER SUGGESTION BOX AS MANDATED BY SECTION 8.6 FOR CONVERSATIONAL LOOP */}
      <section className="py-12 px-4 max-w-7xl mx-auto">
        <div className="bg-gradient-to-br from-[#1C0132] to-[#0A0014] text-white p-8 md:p-12 rounded-3xl relative overflow-hidden shadow-xl">
          {/* Subtle glowing lines representing prestige violet accents */}
          <div className="absolute right-0 bottom-0 w-80 h-80 bg-primary-750/15 blur-3xl rounded-full"></div>
          <div className="absolute left-12 top-12 w-80 h-80 bg-primary-400/10 blur-3xl rounded-full"></div>

          <div className="relative z-10">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
              
              {/* Informational intro column */}
              <div className="md:col-span-5 space-y-4">
                <div className="bg-primary-500/10 border border-primary-500/20 text-primary-200 p-2.5 rounded-full w-11 h-11 flex items-center justify-center">
                  <AlertCircle className="w-5 h-5" />
                </div>
                <h2 className="font-display text-2xl sm:text-3xl font-bold leading-tight text-white">
                  Vous ne trouvez pas un texte juridique ?
                </h2>
                <p className="text-xs text-zinc-400 leading-relaxed font-normal">
                  Notre bibliothèque s'enrichit chaque jour grâce à sa communauté. Manifestez un texte manquant ou signalez une erreur de documentation, nos documentalistes feront l'indexation sous 48 heures.
                </p>
              </div>

              {/* Interactive submission card */}
              <div className="md:col-span-7 bg-white text-zinc-800 p-6 md:p-8 rounded-2xl border border-zinc-150 shadow-xl">
              {suggSubmitted ? (
                <div className="text-center py-10 space-y-4">
                  <div className="inline-flex items-center justify-center bg-emerald-100 text-emerald-800 p-3.5 rounded-full shadow-sm">
                    <CheckCircle className="w-9 h-9 text-emerald-600" />
                  </div>
                  <h3 className="font-display text-xl font-bold text-zinc-900">Demande enregistrée avec succès !</h3>
                  <div className="inline-block bg-primary-50 border border-primary-200 text-primary-900 font-mono text-xs font-bold px-3 py-1.5 rounded-lg shadow-inner">
                    Numéro de ticket : #{lastTicketId || 'DOC-2026-ENR'}
                  </div>
                  <p className="text-xs text-zinc-600 leading-relaxed max-w-md mx-auto">
                    Merci pour votre contribution. Nos documentalistes procèdent à la recherche et à l'indexation de l'acte sous <strong>48 heures</strong>.
                    {isAuthenticated && " Vous pouvez suivre l'avancement dans l'onglet « Mes Requêtes » de votre espace personnel."}
                  </p>
                  <button
                    type="button"
                    onClick={() => setSuggSubmitted(false)}
                    className="inline-flex items-center gap-2 text-xs font-bold text-primary-750 hover:text-primary-950 underline cursor-pointer pt-2"
                  >
                    Transmettre un autre texte ou signalement
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSuggestSubmit} className="space-y-4 text-stone-800">
                  <h3 className="font-semibold text-sm uppercase tracking-wider text-primary-950 border-b pb-2 flex items-center gap-2">
                    Formulaire de notification rapide
                  </h3>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-[10px] font-bold uppercase text-stone-500 mb-1">Type de Saisie</label>
                      <select 
                        value={suggType} 
                        onChange={(e: any) => setSuggType(e.target.value)}
                        className="w-full text-xs p-2.5 bg-stone-50 border border-stone-300 rounded-xl focus:outline-none focus:border-primary-750 focus:bg-white transition-all font-medium"
                      >
                        <option value="TEXTE_MANQUANT">Texte Manquant / Indexation</option>
                        <option value="ERREUR_TEXTE">Signalement d'Erreur</option>
                        <option value="AUTRE">Autre Demande Technique</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-[10px] font-bold uppercase text-stone-500 mb-1">Nature de l'Acte</label>
                      <select 
                        value={suggNature} 
                        onChange={(e) => setSuggNature(e.target.value)}
                        className="w-full text-xs p-2.5 bg-stone-50 border border-stone-300 rounded-xl focus:outline-none focus:border-primary-750 focus:bg-white transition-all font-medium cursor-pointer"
                      >
                        {categoryLoading && availableNatures.length === 0 && (
                          <option value="" disabled>Chargement des natures...</option>
                        )}
                        {availableNatures.map((n) => (
                          <option key={n.value} value={n.value}>
                            {n.label.toUpperCase()}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-[10px] font-bold uppercase text-zinc-500 mb-1 tracking-wider">Référence Légale (Ex: N°2026/12) *</label>
                    <input
                      type="text"
                      placeholder="Indiquez le numéro ou l'intitulé court"
                      value={suggRef}
                      onChange={(e) => setSuggRef(e.target.value)}
                      required
                      className="w-full text-xs p-3 bg-zinc-50 border border-zinc-200 rounded-xl focus:outline-none focus:border-primary-750 placeholder-zinc-400 focus:bg-white transition-all"
                    />
                  </div>

                  <div>
                    <label className="block text-[10px] font-bold uppercase text-zinc-500 mb-1 tracking-wider">Titre de l'Acte / Intitulé *</label>
                    <input
                      type="text"
                      placeholder="Ex: décret portant nomination du directeur général..."
                      value={suggTitle}
                      onChange={(e) => setSuggTitle(e.target.value)}
                      required
                      className="w-full text-xs p-3 bg-zinc-50 border border-zinc-200 rounded-xl focus:outline-none focus:border-primary-750 placeholder-zinc-400 focus:bg-white transition-all"
                    />
                  </div>

                  <div>
                    <label className="block text-[10px] font-bold uppercase text-zinc-500 mb-1 tracking-wider">Description / Précision additionnelle</label>
                    <textarea
                      rows={2}
                      placeholder="Renseignez l'année d'adoption estimée, la date, ou l'erreur constatée"
                      value={suggDesc}
                      onChange={(e) => setSuggDesc(e.target.value)}
                      className="w-full text-xs p-3 bg-zinc-50 border border-zinc-200 rounded-xl focus:outline-none focus:border-primary-750 placeholder-zinc-400 focus:bg-white transition-all"
                    ></textarea>
                  </div>

                  {!isAuthenticated && (
                    <div>
                      <label className="block text-[10px] font-bold uppercase text-zinc-500 mb-1 tracking-wider">Votre E-mail (optionnel, pour être notifié sous 48h)</label>
                      <input
                        type="email"
                        placeholder="nom@exemple.com"
                        value={suggEmail}
                        onChange={(e) => setSuggEmail(e.target.value)}
                        className="w-full text-xs p-3 bg-zinc-50 border border-zinc-200 rounded-xl focus:outline-none focus:border-primary-750 placeholder-zinc-400 focus:bg-white transition-all"
                      />
                    </div>
                  )}

                  <button
                    type="submit"
                    className="w-full bg-primary-750 hover:bg-primary-950 text-white font-bold text-xs uppercase tracking-widest py-3.5 rounded-full transition-colors flex items-center justify-center gap-2 cursor-pointer border-none shadow-md shadow-primary-750/20 hover:shadow-lg"
                  >
                    <Send className="w-3.5 h-3.5" /> Soumettre aux documentalistes
                  </button>
                </form>
              )}
            </div>

          </div>
        </div>
      </div>
    </section>

      {/* Modal de contrôle d'accès IA */}
      <AiAccessDeniedModal
        isOpen={accessModalOpen}
        onClose={() => setAccessModalOpen(false)}
        mode={accessModalMode}
        onLogin={openAuthModal}
      />
    </div>
  );
};
